import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { redirect } from "react-router-dom";

import type { ID } from "@constants";
import { visitHelper } from "@components/organisms/videoVisit";
import type { IRole } from "@/interfaces";
import { useUIStore } from "@/stores/uiStore";
import {
  IAuthStepEnum,
  useAuthStore,
} from "@/templates/authManagement/authStore";
import { path } from "@/routes/path";

interface IUserState {
  isAuthenticated: boolean;
  login: (
    token: string,
    role: IRole,
    id: ID,
    currentFacility: ID,
    isFirstLogin: boolean,
  ) => void;
  logout: () => void;
  role: IRole | null;
  currentFacility: ID | null;
  token: string | null;
  id: ID;
  changeRole: (facilityId: ID, role: IRole) => void;
  isFirstLogin: boolean;
  setIsFirstLogin: (value: boolean) => void;
}

export const useUserStore = create<IUserState>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
          id: "",
          isAuthenticated: false,
          isFirstLogin: false,
          login: (
            token: string,
            role: IRole | null,
            id: ID,
            currentFacility,
            isFirstLogin,
          ) =>
            set({
              isAuthenticated: true,
              token,
              role,
              id,
              currentFacility,
              isFirstLogin,
            }),
          logout: () => {
            visitHelper.close();
            set({ isAuthenticated: false, token: null, role: null });
            useUIStore.setState({ pageMode: "index" });
            useAuthStore.getState().resetState();
            redirect(path.authManagement.auth.link(IAuthStepEnum.LOGIN));
          },
          role: null,
          token: null,
          currentFacility: null,
          changeRole: (facilityId, role) =>
            set({ currentFacility: facilityId, role }),
          setIsFirstLogin: (value: boolean) => set({ isFirstLogin: value }),
        }),
        {
          name: "userState",
          partialize: (state) => ({
            role: state.role,
            token: state.token,
            id: state.id,
            currentFacility: state.currentFacility,
          }),
        },
      ),
      {
        enabled: !import.meta.env.PROD,
        name: "userState",
      },
    ),
  ),
);

export const getUserApiRole = (role: IRole | null) => {
  return role
    ?.replaceAll("-drug", "")
    ?.replaceAll("-image", "")
    ?.replaceAll("-lab", "") as IRole;
};

const initialState = useUserStore.getState();

if (initialState.token) {
  useUserStore.setState({ isAuthenticated: true });
}
